/**
 * One direction's announcement: the switch that decides whether it plays, and
 * the two wordings it can play.
 *
 * Its own file because the notice card renders it twice, and because two copies
 * of a block this size in one component is how the outbound half and the inbound
 * half start quietly drifting apart. `NoticeVariantFields` is the layer below -
 * one wording - and this is the pair of them plus the switch that governs both.
 *
 * Which of the two is live is decided by the RECORDING switch, not by this
 * group, and it is labelled here rather than explained: a recorded call is
 * announced as recorded, and a call that keeps no audio but is read by a model
 * while it happens is announced as processed. Both stay editable, because the
 * recording switch is one click away and an operator should not have to flip it
 * to write the other sentence.
 */
import { useT } from "../../../../i18n/I18nProvider";
import type { NoticeClipInfo, NoticeDirectionSettings, NoticeSlot, NoticeVariant } from "../../../../lib/calls";
import { FieldGroup, Note, ToggleRow } from "../panel/fields";
import { NoticeVariantFields } from "./NoticeVariantFields";

export function NoticeDirectionFields({
  direction,
  value,
  onChange,
  record,
  clips,
  builtInAudio,
}: {
  direction: "outbound" | "inbound";
  value: NoticeDirectionSettings;
  onChange: (next: NoticeDirectionSettings) => void;
  /** The account's recording switch, which decides which wording is the live one. */
  record: boolean;
  clips: Partial<Record<NoticeSlot, NoticeClipInfo>>;
  builtInAudio: Partial<Record<NoticeSlot, string>>;
}) {
  const t = useT();
  const live = record ? "recorded" : "processed";
  const recordedSlot: NoticeSlot = `notice.${direction}.recorded`;
  const processedSlot: NoticeSlot = `notice.${direction}.processed`;

  const set = (patch: Partial<NoticeDirectionSettings>) => onChange({ ...value, ...patch });
  const variant = (key: "recorded" | "processed") => (next: NoticeVariant) => set({ [key]: next });

  return (
    <FieldGroup title={t(`settings.notice.${direction}Group`)}>
      <ToggleRow
        label={t(`settings.notice.${direction}Enable`)}
        hint={t(`settings.notice.${direction}EnableHint`)}
        checked={value.enabled}
        onChange={(enabled) => set({ enabled })}
      />

      {/* Only the wordings are hidden while the direction is off, never the
          switch that turns it back on: a group that collapsed to nothing would
          leave the operator hunting for where the announcement went. */}
      {value.enabled ? (
        <>
          <div className="space-y-3">
            <h5 className="crm-lab !mt-0">
              {t("settings.notice.recorded")}
              {live === "recorded" ? ` - ${t("settings.notice.inUse")}` : ""}
            </h5>
            <Note tone="mut">{t("settings.notice.recordedHint")}</Note>
            <NoticeVariantFields
              slot={recordedSlot}
              value={value.recorded}
              onChange={variant("recorded")}
              savedClip={clips[recordedSlot]}
              builtInAudio={builtInAudio[recordedSlot] ?? ""}
            />
          </div>

          <div className="space-y-3">
            <h5 className="crm-lab !mt-0">
              {t("settings.notice.processed")}
              {live === "processed" ? ` - ${t("settings.notice.inUse")}` : ""}
            </h5>
            <Note tone="mut">{t("settings.notice.processedHint")}</Note>
            <NoticeVariantFields
              slot={processedSlot}
              value={value.processed}
              onChange={variant("processed")}
              savedClip={clips[processedSlot]}
              builtInAudio={builtInAudio[processedSlot] ?? ""}
            />
          </div>
        </>
      ) : null}
    </FieldGroup>
  );
}
